import { CobaltIconSearchOutline } from '@cae/cobalt-react/icons/outline/CobaltIconSearchOutline';
import { useDocuments } from '@features/documents/hooks/use-documents/useDocuments';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import type { DocumentsSearchViewContentProps } from './DocumentsSearchViewContent.types';

import { DocumentsSearchResultsTable } from '../documents-search-results-table/DocumentsSearchResultsTable';
import styles from '../DocumentsSearchView.module.css';

const DocumentsSearchViewContent = React.memo(
  ({
    searchText,
    documentType,
    organizationId,
    canDownload,
  }: Readonly<DocumentsSearchViewContentProps>): React.ReactElement => {
    const { data, isLoading, isError } = useDocuments({
      documentType,
      organizationId,
      searchText: searchText || undefined,
    });

    const fileDocuments = React.useMemo(
      () => (data?.documents ?? []).filter(doc => doc.isFile),
      [data?.documents]
    );

    if (!searchText) {
      return (
        <div className={styles.emptyState} data-testid="search-empty-state">
          <CobaltIconSearchOutline size="large" />
          <span className="body-02">Start typing to search</span>
        </div>
      );
    }

    if (isLoading) {
      return <ContentLoader />;
    }

    if (isError) {
      return <InlineFeedback type="data-error" />;
    }

    if (fileDocuments.length === 0) {
      return <InlineFeedback type="no-search-results" />;
    }

    return (
      <DocumentsSearchResultsTable
        documents={fileDocuments}
        organizationId={organizationId}
        canDownload={canDownload}
      />
    );
  }
);

DocumentsSearchViewContent.displayName = 'DocumentsSearchViewContent';

export { DocumentsSearchViewContent };
